import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase, setAuthErrorHandler } from '../lib/supabase';
import { beginAuthPersistence, completeAuthPersistence } from '../lib/authStorage.mjs';
import toast from 'react-hot-toast';
import {
  getPlanConfig as _getPlanConfig,
  getPlanLimits as _getPlanLimits,
  getPlanFeatures as _getPlanFeatures,
  planAtLeast,
  normalizePlanKey,
  resolveFeatureAccess,
} from '../config/planFeatures';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [featureEntitlements, setFeatureEntitlements] = useState({});
  const [entitlementsReady, setEntitlementsReady] = useState(false);

  // Ref to track whether a user was active — used to detect mid-session sign-out
  // (avoids stale-closure issues inside the onAuthStateChange callback)
  const activeUserRef = useRef(null);

  // Dedup flag: prevents two concurrent session-expiry paths (customFetch 401
  // interceptor + onAuthStateChange SIGNED_OUT) from each showing a toast.
  // Set to true by whichever path fires first; reset when a user signs in.
  const sessionExpiredToastShownRef = useRef(false);
  const intentionalSignOutRef = useRef(false);

  // Fetch user profile from user_profiles table
  const fetchUserProfile = useCallback(async (userId) => {
    if (!userId) return null;
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.maybeSingle();
      if (error) {
        console.error('Error fetching user profile:', error?.message);
        return null;
      }
      return data;
    } catch (err) {
      console.error('fetchUserProfile exception:', err);
      return null;
    }
  }, []);

  // Check admin/owner status via admin_users table
  const checkAdminStatus = useCallback(async (userId) => {
    if (!userId) {
      setIsAdmin(false);
      setIsOwner(false);
      return;
    }
    try {
      const { data, error } = await supabase?.from('admin_users')?.select('role, is_active')?.eq('id', userId)?.eq('is_active', true)?.maybeSingle();

      if (error || !data) {
        setIsAdmin(false);
        setIsOwner(false);
        return;
      }
      setIsAdmin(true);
      setIsOwner(data?.role === 'owner');
    } catch (err) {
      console.error('checkAdminStatus exception:', err);
      setIsAdmin(false);
      setIsOwner(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadEntitlements = async () => {
      if (!user) {
        setFeatureEntitlements({});
        setEntitlementsReady(true);
        return;
      }

      setEntitlementsReady(false);
      const planType = normalizePlanKey(userProfile?.plan_type || userProfile?.tier || 'free');
      try {
        const { data, error } = await supabase
          .from('pricing_tiers')
          .select('*')
        const tier = data?.find((candidate) =>
          normalizePlanKey(candidate?.plan_key || candidate?.tier_name) === planType
        );
        if (cancelled) return;
        if (error) {
          console.error('Error fetching plan entitlements:', error.message);
          setFeatureEntitlements({});
          setEntitlementsReady(true);
          return;
        }
        setFeatureEntitlements(
          tier?.feature_entitlements && typeof tier.feature_entitlements === 'object'
            ? tier.feature_entitlements
            : {}
        );
        setEntitlementsReady(true);
      } catch (err) {
        if (!cancelled) {
          console.error('fetch plan entitlements exception:', err);
          setFeatureEntitlements({});
          setEntitlementsReady(true);
        }
      }
    };
    loadEntitlements();
    return () => { cancelled = true; };
  }, [user, userProfile?.plan_type, userProfile?.tier]);

  // Register a global auth-error handler so any Supabase call that returns 401
  // (expired / invalid token) surfaces a toast and clears the session — even on
  // public pages that don't go through a ProtectedRoute redirect.
  useEffect(() => {
    setAuthErrorHandler(() => {
      if (intentionalSignOutRef.current) return;

      // Only react when a user session was actually active to avoid noise from
      // unauthenticated requests that legitimately return 401.
      if (activeUserRef.current === null) return;

      // Dedup: if the onAuthStateChange SIGNED_OUT path already showed the
      // toast, don't show a second one.
      if (!sessionExpiredToastShownRef.current) {
        sessionExpiredToastShownRef.current = true;
        toast('Your session has expired. Please sign in again.', {
          icon: '🔒',
          duration: 5000,
        });
      }

      // Clear local state immediately so the UI reflects the signed-out state.
      activeUserRef.current = null;
      setUser(null);
      setUserProfile(null);
      setIsAdmin(false);
      setIsOwner(false);
      setFeatureEntitlements({});
      setEntitlementsReady(false);

      // Tell Supabase to discard the stored session too.
      supabase?.auth?.signOut().catch(() => {
        // Ignore sign-out errors — the session is already invalid.
      });
    });

    return () => setAuthErrorHandler(null);
  }, []);

  // Initialize auth state once on mount
  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase?.auth?.getSession();

        if (!mounted) return;

        if (session?.user) {
          setUser(session?.user);
          activeUserRef.current = session.user;
          const [profile] = await Promise.all([
            fetchUserProfile(session?.user?.id),
            checkAdminStatus(session?.user?.id),
          ]);
          if (mounted) setUserProfile(profile);
        } else {
          activeUserRef.current = null;
          setUser(null);
          setUserProfile(null);
          setIsAdmin(false);
          setIsOwner(false);
           setFeatureEntitlements({});
           setEntitlementsReady(false);
        }
      } catch (err) {
        console.error('initAuth error:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    initAuth();

    // Listen for subsequent auth changes (sign in, sign out, token refresh)
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;

        if (session?.user) {
          setUser(session?.user);
          activeUserRef.current = session.user;
          // Reset dedup flag so future expiry events can show a toast again.
          sessionExpiredToastShownRef.current = false;
          // Don't block the state change — fetch profile async
          Promise.all([
            fetchUserProfile(session?.user?.id),
            checkAdminStatus(session?.user?.id),
          ])?.then(([profile]) => {
            if (mounted) setUserProfile(profile);
          });
        } else {
          // If a user was active and we receive SIGNED_OUT, their session expired
          // mid-visit — show a friendly message before the ProtectedRoute redirects.
          // Dedup: only show the toast if the customFetch 401 path hasn't already shown one.
          if (
            event === 'SIGNED_OUT'
            && activeUserRef.current !== null
            && !intentionalSignOutRef.current
            && !sessionExpiredToastShownRef.current
          ) {
            sessionExpiredToastShownRef.current = true;
            toast('Your session has expired. Please sign in again.', {
              icon: '🔒',
              duration: 5000,
            });
          }
          activeUserRef.current = null;
          setUser(null);
          setUserProfile(null);
          setIsAdmin(false);
          setIsOwner(false);
          if (event === 'SIGNED_OUT') intentionalSignOutRef.current = false;
        }

        // Always clear loading after first auth state change
        if (mounted) setLoading(false);
      }
    );

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, [fetchUserProfile, checkAdminStatus]);

  // An administrator can change a creator's plan in another session. Keep the
  // active creator's profile current so limits and feature gates update without
  // requiring a sign-out. Realtime covers active tabs; focus/visibility is a
  // lightweight fallback when a browser or project has Realtime unavailable.
  useEffect(() => {
    if (!user?.id) return undefined;

    let cancelled = false;
    const refreshProfile = async () => {
      const profile = await fetchUserProfile(user.id);
      if (!cancelled && profile) setUserProfile(profile);
    };
    const handleFocus = () => { refreshProfile(); };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refreshProfile();
    };

    const channel = supabase
      ?.channel(`active-profile-${user.id}`)
      ?.on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'user_profiles', filter: `id=eq.${user.id}` },
        (payload) => {
          if (!cancelled && payload?.new) setUserProfile(payload.new);
        },
      )
      ?.subscribe();

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
      if (channel) supabase?.removeChannel(channel);
    };
  }, [user?.id, fetchUserProfile]);

  // Sign up — creates auth user; trigger auto-creates user_profiles row
  const signUp = async (email, password, metadata = {}) => {
    const { data, error } = await supabase?.auth?.signUp({
      email,
      password,
      options: {
        data: metadata,
        emailRedirectTo: `${window.location?.origin}/authentication`,
      },
    });
    if (error) throw error;
    return data;
  };

  // Sign in
  const signIn = async (email, password, rememberMe) => {
    const hasPersistenceChoice = typeof rememberMe === 'boolean';
    if (hasPersistenceChoice) beginAuthPersistence(rememberMe);

    let data;
    try {
      const result = await supabase?.auth?.signInWithPassword({
        email,
        password,
      });
      if (result?.error) throw result.error;
      data = result?.data;
      if (hasPersistenceChoice) completeAuthPersistence(true);
    } catch (error) {
      if (hasPersistenceChoice) completeAuthPersistence(false);
      throw error;
    }

    if (data?.user) {
      const [profile] = await Promise.all([
        fetchUserProfile(data?.user?.id),
        checkAdminStatus(data?.user?.id),
      ]);
      setUserProfile(profile);
    }
    return data;
  };

  // Sign out
  const signOut = async () => {
    intentionalSignOutRef.current = true;
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
      activeUserRef.current = null;
      setUser(null);
      setUserProfile(null);
      setIsAdmin(false);
      setIsOwner(false);
    } catch (error) {
      intentionalSignOutRef.current = false;
      throw error;
    }
  };

  // Send password reset email
  const resetPassword = async (email) => {
    const { error } = await supabase?.auth?.resetPasswordForEmail(email, {
      redirectTo: `${window.location?.origin}/reset-password`,
    });
    if (error) throw error;
  };

  // Update password (for logged-in user)
  const updatePassword = async (newPassword) => {
    const { error } = await supabase?.auth?.updateUser({ password: newPassword });
    if (error) throw error;
  };

  // Sign in with a supported social OAuth provider.
  const signInWithOAuth = async (provider, redirectTo) => {
    const { data, error } = await supabase?.auth?.signInWithOAuth({
      provider,
      options: {
        redirectTo: redirectTo || window.location?.origin,
      },
    });
    if (error) throw error;
    return data;
  };

  const isEmailVerified = () => user?.email_confirmed_at != null;

  // Check if the current account has a specific feature enabled
  // Features: 'white_label', 'client_workspaces'
  const hasFeature = (featureKey) => {
    const planType = userProfile?.plan_type || userProfile?.tier || 'free';
    return resolveFeatureAccess({
      featureKey,
      planType,
      accountFeatures: userProfile?.account_features,
      featureEntitlements,
      entitlementsReady,
      isAdmin,
      isOwner,
    }).allowed;
  };

  // Return the full plan config (limits + features + display) for the current user
  const getPlanConfig = () => {
    const planType = userProfile?.plan_type || userProfile?.tier || 'free';
    return _getPlanConfig(planType);
  };

  // Return plan limits for the current user
  const getPlanLimits = () => {
    const planType = userProfile?.plan_type || userProfile?.tier || 'free';
    return _getPlanLimits(planType);
  };

  // Return features available for the current user's plan
  const getPlanFeatures = () => {
    const planType = userProfile?.plan_type || userProfile?.tier || 'free';
    return _getPlanFeatures(planType);
  };

  // Check if the current user's plan is at least the specified level
  const isPlanAtLeast = (minPlan) => {
    if (isOwner || isAdmin) return true;
    const planType = userProfile?.plan_type || userProfile?.tier || 'free';
    return planAtLeast(planType, minPlan);
  };

  // Derive human-readable account level
  const getAccountLevel = () => {
    if (isOwner) return 'Owner';
    if (isAdmin) return 'Admin';
    const planType = userProfile?.plan_type || userProfile?.tier || 'free';
    const planLabels = {
      free: 'Free',
      starter: 'Starter Pro',
      professional: 'Business',
      enterprise: 'Agency (White Label)',
      demo: 'Demo',
      client: 'Client',
    };
    return (planLabels?.[planType?.toLowerCase()] || planType?.charAt(0)?.toUpperCase() + planType?.slice(1));
  };

  // Refresh profile from DB (call after profile save)
  const refreshUserProfile = useCallback(async () => {
    if (!user?.id) return;
    const profile = await fetchUserProfile(user?.id);
    setUserProfile(profile);
    return profile;
  }, [user?.id, fetchUserProfile]);

  const value = {
    user,
    userProfile,
    setUserProfile,
    loading,
    isAdmin,
    isOwner,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    signInWithOAuth,
    isEmailVerified,
    hasFeature,
    getAccountLevel,
    fetchUserProfile,
    refreshUserProfile,
    // Plan helpers (derived from planFeatures config + userProfile.plan_type)
    getPlanConfig,
    getPlanLimits,
    getPlanFeatures,
    isPlanAtLeast,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
